<script lang="ts">
  import { _ } from 'svelte-i18n';
  import Header from '$lib/components/sections/Header.svelte';
  import Footer from '$lib/components/sections/Footer.svelte';
  import Icon from '$lib/components/primitives/Icon.svelte';
  import Button from '$lib/components/primitives/Button.svelte';
  import Card from '$lib/components/primitives/Card.svelte';

  let companyCopied = $state(false);
  let founderCopied = $state(false);

  function copyToClipboard(text: string, type: 'company' | 'founder') {
    navigator.clipboard.writeText(text).then(() => {
      if (type === 'company') {
        companyCopied = true;
        setTimeout(() => companyCopied = false, 2000);
      } else {
        founderCopied = true;
        setTimeout(() => founderCopied = false, 2000);
      }
    });
  }

  const brandAssets = [
    {
      title: $_('pressKit.assets.primaryLogo.title'),
      description: $_('pressKit.assets.primaryLogo.description'),
      formats: ['SVG'],
      image: '/press/heyweek-logo.svg',
      filename: 'heyweek-logo.svg'
    },
    {
      title: $_('pressKit.assets.logoMark.title'),
      description: $_('pressKit.assets.logoMark.description'),
      formats: ['SVG'],
      image: '/press/heyweek-icon.svg',
      filename: 'heyweek-icon.svg'
    },
    {
      title: $_('pressKit.assets.wordmark.title'),
      description: $_('pressKit.assets.wordmark.description'),
      formats: ['SVG'],
      image: '/press/heyweek-word.svg',
      filename: 'heyweek-word.svg'
    }
  ];

  const screenshots = [
    {
      title: $_('pressKit.screenshots.dashboard.title'),
      description: $_('pressKit.screenshots.dashboard.description'),
      icon: 'layout-dashboard'
    },
    {
      title: $_('pressKit.screenshots.projects.title'),
      description: $_('pressKit.screenshots.projects.description'),
      icon: 'folder'
    },
    {
      title: $_('pressKit.screenshots.timeTracking.title'),
      description: $_('pressKit.screenshots.timeTracking.description'),
      icon: 'clock'
    },
    {
      title: $_('pressKit.screenshots.team.title'),
      description: $_('pressKit.screenshots.team.description'),
      icon: 'users'
    }
  ];

  const companyFacts = [
    { label: $_('pressKit.facts.founded'), value: '2021' },
    { label: $_('pressKit.facts.headquarters'), value: $_('pressKit.facts.headquartersValue') },
    { label: $_('pressKit.facts.teamSize'), value: $_('pressKit.facts.teamSizeValue') },
    { label: $_('pressKit.facts.mission'), value: $_('pressKit.facts.missionValue') }
  ];

  const founderImages = [
    {
      title: $_('pressKit.founder.colorTitle'),
      image: '/press/heyweek-founder-color.jpeg',
      filename: 'heyweek-founder-color.jpeg',
      format: 'JPEG'
    },
    {
      title: $_('pressKit.founder.grayscaleTitle'),
      image: '/press/heyweek-founder-grayscale.jpeg',
      filename: 'heyweek-founder-grayscale.jpeg',
      format: 'JPEG'
    }
  ];
</script>

<svelte:head>
  <title>{$_('pressKit.meta.title')} - Heyweek</title>
  <meta name="description" content={$_('pressKit.meta.description')} />
	<meta property="og:title" content="{$_('pressKit.meta.title')} - Heyweek" />
	<meta property="og:description" content={$_('pressKit.meta.description')} />
  <link rel="canonical" href="https://heyweek.com/press-kit" />
</svelte:head>

<Header />

<div class="press-kit-page">
  <!-- Hero Section -->
  <section class="hero">
    <div class="container">
      <div class="hero__content">
        <div class="hero__badge">
          <Icon name="package" size={16} />
          <span>{$_('pressKit.hero.badge')}</span>
        </div>
        <h1 class="hero__title">{$_('pressKit.hero.title')}</h1>
        <p class="hero__subtitle">
          {$_('pressKit.hero.subtitle')}
        </p>
      </div>
    </div>
  </section>

  <!-- Quick Info Section -->
  <section class="info">
    <div class="container">
      <Card class="info__card">
        <div class="info__grid">
          <div class="info__about">
            <div class="info__header">
              <h2 class="info__title">{$_('pressKit.about.title')}</h2>
              <button 
                class="copy-button"
                onclick={() => copyToClipboard($_('pressKit.about.description1') + '\n\n' + $_('pressKit.about.description2'), 'company')}
                title="Copy to clipboard"
              >
                <Icon name={companyCopied ? 'check' : 'copy'} size={18} />
                {#if companyCopied}
                  <span class="copy-feedback">Copied!</span>
                {/if}
              </button>
            </div>
            <p class="info__text">
              {$_('pressKit.about.description1')}
            </p>
            <p class="info__text">
              {$_('pressKit.about.description2')}
            </p>
          </div>
          <div class="info__facts">
            <h3 class="facts__title">{$_('pressKit.facts.title')}</h3>
            <dl class="facts__list">
              {#each companyFacts as fact}
                <div class="fact">
                  <dt class="fact__label">{fact.label}</dt>
                  <dd class="fact__value">{fact.value}</dd>
                </div>
              {/each}
            </dl>
          </div>
        </div>
      </Card>
    </div>
  </section>

  <!-- Founder Section -->
  <section class="founder">
    <div class="container">
      <div class="section__header">
        <h2 class="section__title">{$_('pressKit.founder.title')}</h2>
        <p class="section__subtitle">{$_('pressKit.founder.subtitle')}</p>
      </div>
      
      <Card class="founder__card">
        <div class="founder__content">
          <div class="founder__bio">
            <div class="founder__header">
              <div>
                <h3 class="founder__name">{$_('pressKit.founder.name')}</h3>
                <p class="founder__role">{$_('pressKit.founder.role')}</p>
              </div>
              <button 
                class="copy-button"
                onclick={() => copyToClipboard($_('pressKit.founder.bio'), 'founder')}
                title="Copy bio to clipboard"
              >
                <Icon name={founderCopied ? 'check' : 'copy'} size={18} />
                {#if founderCopied}
                  <span class="copy-feedback">Copied!</span>
                {/if}
              </button>
            </div>
            <p class="founder__description">
              {$_('pressKit.founder.bio')}
            </p>
          </div>
          <div class="founder__images">
            {#each founderImages as image}
              <div class="founder-image">
                <div class="founder-image__preview">
                  <img src={image.image} alt={image.title} class="founder-image__img" />
                </div>
                <div class="founder-image__info">
                  <p class="founder-image__title">{image.title}</p>
                  <a href={image.image} download={image.filename} class="founder-image__download">
                    <Button variant="ghost" size="sm">
                      <Icon name="download" size={16} />
                      {image.format}
                    </Button>
                  </a>
                </div>
              </div>
            {/each}
          </div>
        </div>
      </Card>
    </div>
  </section>

  <!-- Brand Assets Section -->
  <section class="assets">
    <div class="container">
      <div class="section__header">
        <h2 class="section__title">{$_('pressKit.assets.title')}</h2>
        <p class="section__subtitle">{$_('pressKit.assets.subtitle')}</p>
      </div>
      
      <div class="assets__grid">
        {#each brandAssets as asset}
          <Card class="asset-card">
            <a href={asset.image} target="_blank" rel="noopener noreferrer" class="asset-card__preview-link">
              <div class="asset-card__preview">
                <img src={asset.image} alt={asset.title} class="asset-card__image" />
              </div>
            </a>
            <div class="asset-card__info">
              <h3 class="asset-card__title">{asset.title}</h3>
              <p class="asset-card__description">{asset.description}</p>
              <a href={asset.image} download={asset.filename} class="asset-card__download-link">
                <Button variant="ghost" size="sm">
                  <Icon name="download" size={16} />
                  SVG
                </Button>
              </a>
            </div>
          </Card>
        {/each}
      </div>
    </div>
  </section>

  <!-- Screenshots Section -->
  <section class="screenshots">
    <div class="container">
      <div class="section__header">
        <h2 class="section__title">{$_('pressKit.screenshots.title')}</h2>
        <p class="section__subtitle">{$_('pressKit.screenshots.subtitle')}</p>
      </div>
      
      <div class="screenshots__grid">
        {#each screenshots as screenshot}
          <Card class="screenshot-card">
            <div class="screenshot-card__preview">
              <Icon name={screenshot.icon} size={32} />
            </div>
            <div class="screenshot-card__info">
              <h3 class="screenshot-card__title">{screenshot.title}</h3>
              <p class="screenshot-card__description">{screenshot.description}</p>
            </div>
          </Card>
        {/each}
      </div>
    </div>
  </section>

  <!-- Contact Section -->
  <section class="contact">
    <div class="container">
      <Card class="contact__card">
        <div class="contact__content">
          <Icon name="mail" size={32} />
          <h2 class="contact__title">{$_('pressKit.contact.title')}</h2>
          <p class="contact__text">
            {$_('pressKit.contact.description')}
          </p>
          <div class="contact__emails">
            <a href="mailto:hello@heyweek.com" class="contact__email">
              <Icon name="at-sign" size={16} />
              hello@heyweek.com
            </a>
          </div>
        </div>
      </Card>
    </div>
  </section>
</div>

<Footer />

<style>
  .press-kit-page {
    min-height: 100vh;
  }

  /* Hero Section */
  .hero {
    padding: var(--space-24) 0 var(--space-16);
    background: linear-gradient(to bottom, rgba(255, 94, 126, 0.03), transparent);
  }

  .hero__content {
    max-width: 800px;
    margin: 0 auto;
    text-align: center;
  }

  .hero__badge {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-1) var(--space-3);
    background: rgba(255, 94, 126, 0.1);
    color: rgb(255, 94, 126);
    border-radius: var(--radius-full);
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    margin-bottom: var(--space-4);
  }

  .hero__title {
    font-size: var(--font-size-4xl);
    font-weight: var(--font-weight-bold);
    color: var(--text-primary);
    margin-bottom: var(--space-4);
    letter-spacing: -0.02em;
  }

  .hero__subtitle {
    font-size: var(--font-size-lg);
    color: var(--text-secondary);
    margin-bottom: var(--space-8);
    line-height: 1.6;
  }

  /* Info Section */
  .info {
    padding: var(--space-16) 0;
  }

  .info__grid {
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: var(--space-12);
  }

  .info__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: var(--space-4);
  }

  .info__title {
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin: 0;
  }

  .info__text {
    font-size: var(--font-size-base);
    color: var(--text-secondary);
    line-height: 1.7;
    margin-bottom: var(--space-4);
  }

  .facts__title {
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-4);
  }

  .facts__list {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .fact {
    display: flex;
    justify-content: space-between;
    padding-bottom: var(--space-3);
    border-bottom: 1px solid var(--border-light);
  }

  .fact:last-child {
    border-bottom: none;
  }

  .fact__label {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    font-weight: var(--font-weight-medium);
  }

  .fact__value {
    font-size: var(--font-size-sm);
    color: var(--text-primary);
  }

  /* Sections Common */
  .section__header {
    text-align: center;
    margin-bottom: var(--space-12);
  }

  .section__title {
    font-size: var(--font-size-3xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-3);
  }

  .section__subtitle {
    font-size: var(--font-size-base);
    color: var(--text-secondary);
  }

  /* Founder Section */
  .founder {
    padding: var(--space-16) 0;
  }

  .founder__content {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: var(--space-8);
    align-items: start;
  }

  .founder__bio {
    padding-right: var(--space-6);
  }

  .founder__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: var(--space-4);
  }

  .founder__name {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-2);
  }

  .founder__role {
    font-size: var(--font-size-base);
    color: var(--brand-primary);
    font-weight: var(--font-weight-medium);
  }

  .founder__description {
    font-size: var(--font-size-base);
    color: var(--text-secondary);
    line-height: 1.7;
  }

  .founder__images {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-4);
  }

  .founder-image {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .founder-image__preview {
    position: relative;
    padding-bottom: 100%;
    background: var(--bg-secondary);
    border-radius: var(--radius-md);
    overflow: hidden;
  }

  .founder-image__img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .founder-image__info {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .founder-image__title {
    font-size: var(--font-size-sm);
    color: var(--text-primary);
    font-weight: var(--font-weight-medium);
  }

  .founder-image__download {
    align-self: flex-start;
  }

  @media (max-width: 768px) {
    .founder__content {
      grid-template-columns: 1fr;
      gap: var(--space-6);
    }

    .founder__bio {
      padding-right: 0;
    }

    .founder__images {
      grid-template-columns: 1fr;
    }
  }

  /* Assets Section */
  .assets {
    padding: var(--space-16) 0;
    background: var(--bg-secondary);
  }

  .assets__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: var(--space-6);
  }

  .asset-card__preview-link {
    display: block;
    text-decoration: none;
    cursor: pointer;
    transition: opacity 0.2s ease;
  }

  .asset-card__preview-link:hover {
    opacity: 0.8;
  }

  .asset-card__preview {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 120px;
    padding: var(--space-4);
    background: var(--bg-primary);
    border: 1px solid var(--border-light);
    border-radius: var(--radius-md);
    margin-bottom: var(--space-4);
    transition: border-color 0.2s ease;
  }

  .asset-card__preview-link:hover .asset-card__preview {
    border-color: var(--accent-primary);
  }

  .asset-card__image {
    max-width: 100%;
    max-height: 100%;
    height: auto;
    width: auto;
    object-fit: contain;
  }

  .asset-card__info {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .asset-card__title {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin: 0;
  }

  .asset-card__description {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    margin: 0;
  }

  .asset-card__download-link {
    align-self: flex-start;
    text-decoration: none;
  }

  /* Screenshots Section */
  .screenshots {
    padding: var(--space-16) 0;
  }

  .screenshots__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: var(--space-6);
  }

  .screenshot-card__preview {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 160px;
    background: linear-gradient(135deg, rgba(255, 94, 126, 0.05), rgba(255, 94, 126, 0.1));
    color: rgb(255, 94, 126);
  }

  .screenshot-card__info {
    padding: var(--space-6);
  }

  .screenshot-card__title {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-1);
  }

  .screenshot-card__description {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
  }

  /* Contact Section */
  .contact {
    padding: var(--space-16) 0;
  }

  .contact__content {
    text-align: center;
    max-width: 600px;
    margin: 0 auto;
  }

  .contact__content > :global(svg) {
    color: rgb(255, 94, 126);
    margin-bottom: var(--space-4);
  }

  .contact__title {
    font-size: var(--font-size-2xl);
    font-weight: var(--font-weight-semibold);
    color: var(--text-primary);
    margin-bottom: var(--space-3);
  }

  .contact__text {
    font-size: var(--font-size-base);
    color: var(--text-secondary);
    margin-bottom: var(--space-6);
    line-height: 1.6;
  }

  .contact__emails {
    display: flex;
    gap: var(--space-4);
    justify-content: center;
    flex-wrap: wrap;
  }

  .contact__email {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    color: rgb(255, 94, 126);
    text-decoration: none;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    transition: opacity 0.2s ease;
  }

  .contact__email:hover {
    opacity: 0.8;
  }

  /* Copy Button */
  .copy-button {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2);
    background: transparent;
    border: 1px solid var(--border-light);
    border-radius: var(--radius-md);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.2s ease;
    position: relative;
  }

  .copy-button:hover {
    background: var(--bg-secondary);
    color: var(--text-primary);
    border-color: var(--accent-primary);
  }

  .copy-feedback {
    position: absolute;
    top: -28px;
    right: 0;
    background: var(--accent-primary);
    color: white;
    padding: var(--space-1) var(--space-2);
    border-radius: var(--radius-sm);
    font-size: var(--font-size-xs);
    font-weight: var(--font-weight-medium);
    white-space: nowrap;
    animation: fadeIn 0.2s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* Responsive Design */
  @media (max-width: 768px) {
    .hero__title {
      font-size: var(--font-size-3xl);
    }

    .info__grid {
      grid-template-columns: 1fr;
      gap: var(--space-8);
    }

    .assets__grid,
    .screenshots__grid {
      grid-template-columns: 1fr;
    }

    .contact__emails {
      flex-direction: column;
      align-items: center;
    }
  }
</style>